import { useRef } from 'react';
import { useParams, Link, NavLink } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { getCompanyBySlug, companyPath, ROUTE_PATHS } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function CompanyHome() {
  const { slug } = useParams<{ slug: string }>();
  const company = getCompanyBySlug(slug ?? '');

  if (!company) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="eyebrow mb-4">Company Not Found</p>
            <Link to={ROUTE_PATHS.COMPANIES} className="btn-gold-outline">View All Companies</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const subNav = [
    { label: 'About', to: companyPath(company.slug) + '/about' },
    { label: 'Services', to: companyPath(company.slug) + '/services' },
    { label: 'Portfolio', to: companyPath(company.slug) + '/portfolio' },
    { label: 'Contact', to: companyPath(company.slug) + '/contact' },
  ];

  return (
    <Layout activeCompanySlug={company.slug}>
      {/* ── Hero ── */}
      <section className="relative w-full h-screen min-h-[640px] overflow-hidden flex items-center">
        <div className="absolute inset-0">
          <img
            src={company.heroImage}
            alt={company.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.6)] via-[rgba(8,8,8,0.3)] to-[rgba(8,8,8,0.8)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,8,8,0.7)] via-transparent to-transparent" />
        </div>

        <div className="glow-orb w-[500px] h-[300px] bg-[rgba(201,168,76,0.05)] top-1/3 left-1/3" />

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div
                className="w-10 h-10 flex items-center justify-center border text-[10px] font-bold tracking-widest"
                style={{ borderColor: 'rgba(201,168,76,0.6)', color: '#c9a84c', fontFamily: 'var(--font-sans)' }}
              >
                {company.logoInitials}
              </div>
              <p className="eyebrow">{company.name}</p>
            </div>
            <h1 className="heading-display text-[clamp(3rem,8vw,7rem)] text-[#f5f5f5] max-w-4xl mb-5">
              {company.tagline.split(' ').map((word, i) =>
                i === company.tagline.split(' ').length - 1
                  ? <span key={i} className="text-gold italic"> {word}</span>
                  : <span key={i}>{word} </span>
              )}
            </h1>
            <div className="divider-gold-left" />
            <p className="text-[#f5f5f5]/55 text-base md:text-lg max-w-xl leading-relaxed mt-5 mb-10">
              {company.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={companyPath(company.slug) + '/services'} className="btn-gold-fill">
                Our Services
              </Link>
              <Link to={companyPath(company.slug) + '/contact'} className="btn-gold-outline">
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats bar ── */}
      <section className="bg-[#0c0c0c] border-y border-[rgba(201,168,76,0.1)] py-12">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-[rgba(201,168,76,0.1)]">
            {company.stats.map((stat, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className="text-center md:px-8">
                  <p className="heading-display text-[clamp(2rem,4vw,3rem)] text-gold">{stat.value}</p>
                  <p className="eyebrow opacity-55 mt-1">{stat.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── About section ── */}
      <section className="py-28 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <FadeUp>
              <p className="eyebrow mb-4">About {company.shortName}</p>
              <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5] mb-6">
                Who We Are
              </h2>
              <div className="divider-gold-left" />
              <p className="text-[#f5f5f5]/55 text-sm leading-relaxed mt-6">
                {company.longDescription}
              </p>
              <Link
                to={companyPath(company.slug) + '/about'}
                className="inline-flex items-center gap-2 mt-7 text-[11px] tracking-[0.2em] uppercase text-[#c9a84c] hover:text-[#f0c040] transition-colors duration-300"
              >
                Full Story <ArrowRight size={12} />
              </Link>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="grid grid-cols-2 gap-3">
                {company.portfolioItems.slice(0, 4).map((item, i) => (
                  <div key={i} className="card-editorial h-52">
                    <img src={item.image} alt={item.title} loading="lazy" />
                    <div className="card-overlay" />
                    <div className="absolute bottom-3 left-3">
                      <p className="text-[9px] tracking-[0.15em] uppercase text-[#c9a84c]/70">{item.year}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ── Services preview ── */}
      <section className="py-24 bg-[#060606]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-14 gap-6">
            <div>
              <p className="eyebrow mb-3">What We Do</p>
              <h2 className="heading-display text-[clamp(2rem,4vw,3rem)] text-[#f5f5f5]">Our Services</h2>
            </div>
            <Link to={companyPath(company.slug) + '/services'} className="btn-gold-outline whitespace-nowrap">
              All Services
            </Link>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(201,168,76,0.08)]">
            {company.services.map((service, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="bg-[#090909] p-8 lg:p-10 group hover:bg-[rgba(201,168,76,0.03)] transition-colors duration-400">
                  <div className="w-10 h-10 border border-[rgba(201,168,76,0.3)] flex items-center justify-center mb-5">
                    <div className="w-2 h-2 bg-[#c9a84c] opacity-70" />
                  </div>
                  <h3 className="heading-display text-2xl text-[#f5f5f5] mb-3">{service.title}</h3>
                  <div className="divider-gold-left" />
                  <p className="text-[#f5f5f5]/50 text-sm leading-relaxed mt-4">{service.description}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── Portfolio preview ── */}
      <section className="py-24 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-14 gap-6">
            <div>
              <p className="eyebrow mb-3">Our Work</p>
              <h2 className="heading-display text-[clamp(2rem,4vw,3rem)] text-[#f5f5f5]">Portfolio</h2>
            </div>
            <Link to={companyPath(company.slug) + '/portfolio'} className="btn-gold-outline whitespace-nowrap">
              View All
            </Link>
          </FadeUp>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {company.portfolioItems.map((item, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className={`card-editorial ${i === 0 ? 'h-80 col-span-2 row-span-1' : 'h-56'}`}>
                  <img src={item.image} alt={item.title} loading="lazy" />
                  <div className="card-overlay" />
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    <p className="text-[9px] tracking-[0.15em] uppercase text-[#c9a84c]/70 mb-1">{item.category}</p>
                    <h4 className="heading-display text-lg text-[#f5f5f5] leading-tight">{item.title}</h4>
                    <p className="text-[9px] tracking-[0.1em] text-[#f5f5f5]/40 mt-1">{item.year}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={company.heroImage}
            alt="CTA"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-[rgba(8,8,8,0.85)]" />
        </div>
        <div className="glow-orb w-[400px] h-[200px] bg-[rgba(201,168,76,0.08)] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <FadeUp>
            <p className="eyebrow mb-5">{company.name}</p>
            <h2 className="heading-display text-[clamp(2.5rem,5vw,4.5rem)] text-[#f5f5f5] mb-6">
              Ready to Work With Us?
            </h2>
            <div className="divider-gold mb-8" />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={companyPath(company.slug) + '/contact'} className="btn-gold-fill">Get in Touch</Link>
              <Link to={ROUTE_PATHS.HOME} className="btn-gold-outline">Back to Hub</Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </Layout>
  );
}
